import React, { useMemo, useState } from 'react';
import { Transaction, ManagedDocument } from '../../types';
import { Landmark, AlertCircle, CheckCircle2, FileText, Receipt as ReceiptIcon, Search, Link2Off, Loader2, X, Trash2, ArrowRightLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { format } from 'date-fns';
import { removeTransactionLink } from '../../services/managementService';
import { calculateTransactionBalances } from '../../utils/transactionBalance';

interface TransactionAuditReportProps {
  userId: string;
  transactions: Transaction[];
  documents: ManagedDocument[];
}

export const TransactionAuditReport: React.FC<TransactionAuditReportProps> = ({ userId, transactions, documents }) => {
  const [filter, setFilter] = useState<'ALL' | 'ORPHAN' | 'LINKED' | 'PARTIAL' | 'PENDING'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{docId: string, txId: string, docLabel: string} | null>(null);

  const handleUnlink = async () => {
    if (!confirmDelete) return;
    const { docId, txId } = confirmDelete;
    try {
      setUnlinkingId(`${txId}-${docId}`);
      setConfirmDelete(null);
      await removeTransactionLink(userId, docId, txId);
    } catch (error: any) {
      alert("Error al desvincular: " + error.message);
    } finally {
      setUnlinkingId(null);
    }
  };

  const enrichedTransactions = useMemo(() => {
    const balances = calculateTransactionBalances(transactions, documents);

    return transactions
      .filter(tx => tx.type !== 'TRANSFER')
      .map(tx => {
        const tInfo = balances[tx.id];
        const linkedDocs = tInfo 
          ? tInfo.allocations.map(alloc => {
              const doc = documents.find(d => d.id === alloc.docId)!;
              return { ...doc, __allocatedAmount: alloc.amount }; 
            }).filter(d => !!d.id)
          : [];
          
        const totalAmount = tInfo ? tInfo.totalAmount : tx.details.reduce((sum, det) => sum + det.amount, 0);
        const totalLinkedAmount = tInfo ? tInfo.usedAmount : 0;
        const pendingBalance = tInfo ? tInfo.availableAmount : totalAmount;
        
        let status: 'ORPHAN' | 'PARTIAL' | 'LINKED' = 'ORPHAN';
        if (linkedDocs.length > 0) {
          if (pendingBalance > 1) { // Small threshold for float rounding
            status = 'PARTIAL';
          } else {
            status = 'LINKED';
          }
        }

        return {
          ...tx,
          linkedDocs,
          totalAmount,
          totalLinkedAmount,
          pendingBalance,
          status
        };
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, documents]);

  const filteredTransactions = useMemo(() => {
    return enrichedTransactions.filter(tx => {
      const matchesSearch = tx.description.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      if (filter === 'ORPHAN') return tx.status === 'ORPHAN';
      if (filter === 'LINKED') return tx.status === 'LINKED';
      if (filter === 'PARTIAL') return tx.status === 'PARTIAL';
      if (filter === 'PENDING') return tx.pendingInvoice === true;
      return true;
    });
  }, [enrichedTransactions, filter, searchTerm]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const orphanCount = enrichedTransactions.filter(tx => tx.status === 'ORPHAN').length;
  const linkedCount = enrichedTransactions.filter(tx => tx.status === 'LINKED').length;
  const partialCount = enrichedTransactions.filter(tx => tx.status === 'PARTIAL').length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-3xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
            <AlertCircle size={64} className="text-amber-500" />
          </div>
          <p className="text-amber-400 text-[10px] font-bold uppercase tracking-widest mb-1">
            Movimientos Huérfanos
          </p>
          <div className="text-3xl font-bold text-white tracking-tight">
            {orphanCount}
          </div>
        </div>

        <div className="bg-indigo-500/10 border border-indigo-500/20 p-6 rounded-3xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
            <ArrowRightLeft size={64} className="text-indigo-500" />
          </div>
          <p className="text-indigo-400 text-[10px] font-bold uppercase tracking-widest mb-1">
            Parcialmente Vinculados
          </p>
          <div className="text-3xl font-bold text-white tracking-tight">
            {partialCount}
          </div>
        </div>
        
        <div className="bg-emerald-500/10 border border-emerald-500/20 p-6 rounded-3xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
            <CheckCircle2 size={64} className="text-emerald-500" />
          </div>
          <p className="text-emerald-500 text-[10px] font-bold uppercase tracking-widest mb-1">
            Vinculados (Total)
          </p>
          <div className="text-3xl font-bold text-white tracking-tight">
            {linkedCount}
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden">
        <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-white">Auditoría de Enlaces (Gestión ↔ Finanzas)</h2>
            <p className="text-xs text-zinc-500 mt-1">Verifica a qué documento o recibo está aplicado cada movimiento de caja/banco.</p>
          </div>
          <div className="flex items-center gap-3">
             <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <input 
                  type="text" 
                  placeholder="Buscar descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 rounded-xl pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 w-full md:w-48"
                />
             </div>
             <select
               value={filter}
               onChange={(e) => setFilter(e.target.value as any)}
               className="bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2 text-sm text-zinc-400 focus:outline-none focus:border-indigo-500"
             >
               <option value="ALL">Todos</option>
               <option value="LINKED">Solo Vinculados</option>
               <option value="PARTIAL">Parcialmente Vinculados</option>
               <option value="ORPHAN">Solo Huérfanos</option>
               <option value="PENDING">Pendientes de Factura</option>
             </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-950/50">
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Fecha y Movimiento</th>
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Información Financiera</th>
                <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Estado y Enlaces (Gestión)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-zinc-500 text-sm">
                    No se encontraron transacciones con los filtros actuales.
                  </td>
                </tr>
              ) : (
                filteredTransactions.map(tx => {
                  return (
                    <tr key={tx.id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-white mb-1">
                          {tx.description}
                          {tx.pendingInvoice && (
                            <span className="ml-2 inline-flex items-center gap-1.5 bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-widest uppercase align-middle">
                               Pendiente
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                           <span className="text-xs text-zinc-400 font-mono">{format(new Date(tx.date), 'dd/MM/yyyy')}</span>
                           <span className="text-[10px] text-zinc-600 font-mono hidden md:inline">ID: {tx.id}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="space-y-1">
                          <div className="text-sm font-bold font-mono text-white">
                            {formatCurrency(tx.totalAmount)}
                          </div>
                          {tx.status !== 'ORPHAN' && (
                            <div className="text-[10px] font-mono">
                              <div className="text-indigo-400">Enlazado: {formatCurrency(tx.totalLinkedAmount)}</div>
                              <div className={tx.pendingBalance > 0 ? "text-amber-500" : "text-zinc-600"}>
                                Saldo: {formatCurrency(tx.pendingBalance)}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {tx.status === 'ORPHAN' ? (
                          <div className="inline-flex items-center gap-1.5 bg-amber-500/10 text-amber-500 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase border border-amber-500/20">
                            <AlertCircle size={12} /> No Vinculado (Huérfano)
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 mb-1">
                              {tx.status === 'PARTIAL' ? (
                                <div className="inline-flex items-center gap-1.5 bg-indigo-500/10 text-indigo-400 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase border border-indigo-500/20">
                                  <ArrowRightLeft size={12} /> Parcialmente Vinculado
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-500 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase border border-emerald-500/20">
                                  <CheckCircle2 size={12} /> Vinculado
                                </div>
                              )}
                            </div>
                            {tx.linkedDocs.map(doc => (
                              <div key={doc.id} className="flex items-start gap-2 bg-indigo-500/5 p-2 border border-indigo-500/10 rounded-lg">
                                <FileText size={14} className="text-indigo-400 mt-0.5" />
                                <div>
                                  <div className="text-xs font-bold text-indigo-400 leading-tight">Doc. {doc.type} ({doc.number || 'S/N'})</div>
                                  <div className="text-[10px] text-zinc-400 mt-0.5">Monto Asignado: {formatCurrency((doc as any).__allocatedAmount || doc.totalAmount)}</div>
                                  <div className="text-[10px] text-zinc-500">Contacto: {doc.contactName}</div>
                                </div>
                                <button
                                  onClick={() => {
                                    setConfirmDelete({
                                      docId: doc.id,
                                      txId: tx.id,
                                      docLabel: `${doc.type} (${doc.number || 'SN'})`
                                    });
                                  }}
                                  disabled={unlinkingId === `${tx.id}-${doc.id}`}
                                  className="ml-auto p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                  title="Desvincular movimiento"
                                >
                                  {unlinkingId === `${tx.id}-${doc.id}` ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <Link2Off size={14} />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {confirmDelete && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl max-w-md w-full shadow-2xl"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 mb-2">
                  <Link2Off size={32} />
                </div>
                <h3 className="text-xl font-bold text-white">¿Confirmar Desvinculación?</h3>
                <p className="text-zinc-400 text-sm">
                  Estás a punto de desvincular el documento <span className="text-white font-bold">{confirmDelete.docLabel}</span> de este movimiento financiero.
                  El documento volverá a quedar pendiente o su saldo se ajustará según corresponda.
                </p>
                <div className="flex gap-3 w-full mt-6">
                  <button 
                    onClick={() => setConfirmDelete(null)}
                    className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold rounded-xl transition-all"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleUnlink}
                    className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <Trash2 size={18} />
                    Desvincular
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
